import { useState, useEffect, useMemo, useRef } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { productionApi } from '../../api/production.api';
import type { InputPurchase } from '../../types/production.types';

const CATEGORY_LABELS: Record<string, string> = {
  SEMENCES: 'Semences',
  PLANTS: 'Plants',
  ENGRAIS: 'Engrais',
  TERREAU: 'Terreau',
  PHYTO: 'Phyto',
  AUTRE: 'Autre',
};

const CATEGORY_BADGES: Record<string, string> = {
  SEMENCES: 'bg-green-100 text-green-700',
  PLANTS: 'bg-emerald-100 text-emerald-700',
  ENGRAIS: 'bg-amber-100 text-amber-700',
  TERREAU: 'bg-yellow-100 text-yellow-800',
  PHYTO: 'bg-purple-100 text-purple-700',
  AUTRE: 'bg-gray-100 text-gray-700',
};

const EMPTY_FORM = {
  category: 'SEMENCES',
  supplier_name: '',
  description: '',
  purchase_date: new Date().toISOString().slice(0, 10),
  invoice_number: '',
  total_euros: '',
  is_certified_bio: true,
  certification: '',
  notes: '',
};

function formatEuros(cents?: number | null): string {
  if (cents == null) return '—';
  return (cents / 100).toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/**
 * Réduit une image volumineuse (photo de téléphone) avant l'upload — sinon
 * une photo de plusieurs Mo dépasse la limite serveur et le flux est tronqué
 * (« Multipart: Unexpected end of form »). Les PDF et HEIC passent tels quels.
 */
async function downscaleForUpload(file: File): Promise<File> {
  const canvasDecodable = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!canvasDecodable.includes(file.type)) return file; // PDF, HEIC… → serveur
  if (file.size < 1_500_000) return file; // déjà léger

  try {
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('decode'));
        img.src = url;
      });
      const MAX = 2000;
      const scale = Math.min(1, MAX / Math.max(img.naturalWidth, img.naturalHeight));
      const w = Math.round(img.naturalWidth * scale);
      const h = Math.round(img.naturalHeight * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return file;
      ctx.drawImage(img, 0, 0, w, h);
      const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', 0.82));
      if (!blob) return file;
      return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch {
    return file;
  }
}

/**
 * Registre des achats d'intrants production (semences, plants, engrais,
 * terreau, phyto) avec facture archivée — justificatifs contrôle bio.
 */
export default function InputPurchases() {
  const { tenant, loading: tenantLoading } = useTenant();

  const [purchases, setPurchases] = useState<InputPurchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<InputPurchase | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTargetRef = useRef<string | null>(null);

  useEffect(() => {
    if (!tenantLoading && tenant) loadData();
  }, [tenant, tenantLoading]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await productionApi.listInputPurchases();
      setPurchases(data);
    } catch (err: any) {
      alert('Erreur: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const years = useMemo(() => {
    const set = new Set(purchases.map((p) => new Date(p.purchase_date).getFullYear()));
    return Array.from(set).sort((a, b) => b - a);
  }, [purchases]);

  const filtered = useMemo(() => {
    return purchases.filter((p) => {
      if (categoryFilter && p.category !== categoryFilter) return false;
      if (yearFilter && new Date(p.purchase_date).getFullYear() !== parseInt(yearFilter)) return false;
      return true;
    });
  }, [purchases, categoryFilter, yearFilter]);

  const totalCents = filtered.reduce((sum, p) => sum + (p.total_cents || 0), 0);

  const openModal = (p?: InputPurchase) => {
    if (p) {
      setEditing(p);
      setForm({
        category: p.category,
        supplier_name: p.supplier_name,
        description: p.description,
        purchase_date: p.purchase_date.slice(0, 10),
        invoice_number: p.invoice_number || '',
        total_euros: p.total_cents != null ? (p.total_cents / 100).toString().replace('.', ',') : '',
        is_certified_bio: p.is_certified_bio,
        certification: p.certification || '',
        notes: p.notes || '',
      });
    } else {
      setEditing(null);
      setForm({ ...EMPTY_FORM });
    }
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.supplier_name.trim() || !form.description.trim() || !form.purchase_date) {
      alert('Fournisseur, description et date sont obligatoires');
      return;
    }
    const euros = form.total_euros.trim().replace(',', '.');
    const totalCentsValue = euros ? Math.round(parseFloat(euros) * 100) : undefined;
    if (euros && Number.isNaN(totalCentsValue)) {
      alert('Montant invalide');
      return;
    }

    const payload: any = {
      category: form.category,
      supplier_name: form.supplier_name.trim(),
      description: form.description.trim(),
      purchase_date: form.purchase_date,
      invoice_number: form.invoice_number.trim() || null,
      total_cents: totalCentsValue ?? null,
      is_certified_bio: form.is_certified_bio,
      certification: form.certification.trim() || null,
      notes: form.notes.trim() || null,
    };

    try {
      setSaving(true);
      if (editing) {
        await productionApi.updateInputPurchase(editing.id, payload);
      } else {
        await productionApi.createInputPurchase(payload);
      }
      setModalOpen(false);
      await loadData();
    } catch (err: any) {
      alert('Erreur: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p: InputPurchase) => {
    if (!window.confirm(`Supprimer l'achat « ${p.description} » (${p.supplier_name}) ?`)) return;
    try {
      await productionApi.deleteInputPurchase(p.id);
      await loadData();
    } catch (err: any) {
      alert('Erreur: ' + err.message);
    }
  };

  const triggerUpload = (id: string) => {
    uploadTargetRef.current = id;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const targetId = uploadTargetRef.current;
    e.target.value = '';
    if (!file || !targetId) return;
    try {
      setUploadingId(targetId);
      const prepared = await downscaleForUpload(file);
      await productionApi.attachInputPurchaseDocument(targetId, prepared);
      await loadData();
    } catch (err: any) {
      alert('Erreur upload: ' + (err.response?.data?.message || err.message));
    } finally {
      setUploadingId(null);
    }
  };

  if (tenantLoading || loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-4 border-green-200 border-t-green-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Achats d'intrants</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Semences, plants, engrais, terreau — factures archivées (justificatifs contrôle bio)
          </p>
        </div>
        <button onClick={() => openModal()} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 text-sm">
          + Nouvel achat
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setCategoryFilter('')}
          className={`px-3 py-1.5 rounded-full text-sm ${!categoryFilter ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
        >
          Tous
        </button>
        {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setCategoryFilter(categoryFilter === key ? '' : key)}
            className={`px-3 py-1.5 rounded-full text-sm ${categoryFilter === key ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            {label}
          </button>
        ))}
        <select className="input w-36 ml-auto" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
          <option value="">Toutes années</option>
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {/* Summary */}
      <div className="text-sm text-gray-500">
        {filtered.length} achat{filtered.length > 1 ? 's' : ''}
        {totalCents > 0 && <> · total {formatEuros(totalCents)}</>}
        {' '}· {filtered.filter((p) => p.document_url).length} facture{filtered.filter((p) => p.document_url).length > 1 ? 's' : ''} jointe{filtered.filter((p) => p.document_url).length > 1 ? 's' : ''}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            Aucun achat d'intrant enregistré — commencez par « + Nouvel achat »
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr className="text-left text-xs text-gray-500 uppercase">
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Catégorie</th>
                <th className="px-4 py-2">Fournisseur</th>
                <th className="px-4 py-2">Description</th>
                <th className="px-4 py-2">N° facture</th>
                <th className="px-4 py-2 text-right">Montant</th>
                <th className="px-4 py-2">Bio</th>
                <th className="px-4 py-2">Facture</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 whitespace-nowrap">
                    {new Date(p.purchase_date).toLocaleDateString('fr-BE')}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${CATEGORY_BADGES[p.category] || CATEGORY_BADGES.AUTRE}`}>
                      {CATEGORY_LABELS[p.category] || p.category}
                    </span>
                  </td>
                  <td className="px-4 py-2 font-medium">{p.supplier_name}</td>
                  <td className="px-4 py-2 max-w-xs">
                    <span className="line-clamp-2">{p.description}</span>
                    {p.notes && <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{p.notes}</p>}
                  </td>
                  <td className="px-4 py-2 text-gray-500">{p.invoice_number || '—'}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">{formatEuros(p.total_cents)}</td>
                  <td className="px-4 py-2">
                    {p.is_certified_bio ? (
                      <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded" title={p.certification || undefined}>
                        ✓ {p.certification || 'Bio'}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">non bio</span>
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {p.document_url ? (
                      <a
                        href={p.document_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-green-700 hover:underline text-xs"
                        title={p.document_filename || undefined}
                      >
                        📎 Voir
                      </a>
                    ) : (
                      <button
                        onClick={() => triggerUpload(p.id)}
                        disabled={uploadingId === p.id}
                        className="text-xs text-gray-500 border border-dashed border-gray-300 px-2 py-1 rounded hover:border-green-500 hover:text-green-700 disabled:opacity-50"
                      >
                        {uploadingId === p.id ? '⏳...' : '⬆️ Joindre'}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {p.document_url && (
                      <button onClick={() => triggerUpload(p.id)} disabled={uploadingId === p.id} className="text-gray-400 hover:text-gray-600 text-sm mr-2" title="Remplacer la facture">
                        {uploadingId === p.id ? '⏳' : '🔄'}
                      </button>
                    )}
                    <button onClick={() => openModal(p)} className="text-gray-400 hover:text-gray-600 text-sm mr-2">✏️</button>
                    <button onClick={() => handleDelete(p)} className="text-gray-400 hover:text-red-600 text-sm">🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Hidden file input (shared) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleFileSelected}
      />

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h2 className="font-semibold">{editing ? "Modifier l'achat" : 'Nouvel achat d\'intrant'}</h2>
              <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Catégorie</label>
                  <select className="input w-full" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Date d'achat *</label>
                  <input type="date" className="input w-full" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Fournisseur *</label>
                <input type="text" className="input w-full" placeholder="Semailles, DCM, Peltracom..." value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Description *</label>
                <input type="text" className="input w-full" placeholder="Terreau semis bio 70 L × 10" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">N° facture</label>
                  <input type="text" className="input w-full" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Montant TTC (€)</label>
                  <input type="text" inputMode="decimal" className="input w-full" placeholder="123,45" value={form.total_euros} onChange={(e) => setForm({ ...form, total_euros: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 items-end">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.is_certified_bio} onChange={(e) => setForm({ ...form, is_certified_bio: e.target.checked })} />
                  Intrant certifié bio / utilisable en AB
                </label>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Certification (mention facture)</label>
                  <input type="text" className="input w-full" placeholder="AB, Ecocert..." value={form.certification} onChange={(e) => setForm({ ...form, certification: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
                <textarea className="input w-full" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              {!editing && (
                <p className="text-xs text-gray-400">
                  💡 Après l'enregistrement, joignez la facture (PDF ou photo) via le bouton « ⬆️ Joindre » de la ligne.
                </p>
              )}
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">Annuler</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
